import { forwardRef } from 'react';

import { CircularProgress } from '@mui/material';

import { SPINNER_SIZE_WITH_TEXT_PX } from '../../../constants/buttons';
import { BLACK_COLOR, WHITE_COLOR } from '../../../constants/colors';
import { IButton } from '../../../interfaces/buttons';
import type { ButtonVariant } from '../../../types/buttons';
import { toKebabCase } from '../../../utils/utils';
import './CustomButton.scss';

const spinnerColors: Record<ButtonVariant, { color: string }> = {
  default: { color: BLACK_COLOR },
  primary: { color: WHITE_COLOR },
  secondary: { color: BLACK_COLOR },
  ghost: { color: BLACK_COLOR },
  danger: { color: BLACK_COLOR },
  lost: { color: BLACK_COLOR }
};

const CustomButton = forwardRef<HTMLButtonElement, IButton>(
  (
    { variant = 'default', size = 'm', className, type = 'button', loading, loadingText, children, disabled, ...rest },
    ref
  ) => {
    const classNames = [
      'custom-button',
      `custom-button--${toKebabCase(variant)}`,
      `custom-button--${size}`,
      loading ? 'custom-button--loading' : '',
      className
    ]
      .filter(Boolean)
      .join(' ');
    const spinnerSize = loadingText ? (SPINNER_SIZE_WITH_TEXT_PX[size] ?? 14) : 20;

    const spinner = <CircularProgress sx={{ ...spinnerColors[variant] }} size={spinnerSize} />;

    return (
      <button ref={ref} type={type} disabled={loading || disabled} className={classNames} {...rest}>
        <span className="custom-button__content">{children}</span>
        {loading && (
          <span className="custom-button__spinner">
            {spinner}
            {loadingText && <span className="custom-button__loading-text">{loadingText}</span>}
          </span>
        )}
      </button>
    );
  }
);

CustomButton.displayName = 'CustomButton';

export default CustomButton;
